// @flow

import _ from 'lodash/fp';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Redirect} from 'react-router-dom';
import {
  clear,
  reset,
  fetchData,
  fetchCustomRow,
  createCustomRow,
  updateCustomRow,
  removeCustomRow,
  customRowRemoveFile,
  customRowUploadFile,
  customRowAbortFileUpload,
} from '../../actions/customRowView';
import {touch, isValid, getFormValues, reset as resetForm} from 'redux-form';
import {setDirty} from '../../actions/form';
import SaveMenu from '../../components/SaveMenu/SaveMenu';
import CustomRowForm from '../../components/CustomRowForm/CustomRowForm';
import ServerError from '../../components/ServerError/ServerError';
import LoadingIndicator from '../../components/LoadingIndicator/LoadingIndicator';
import Button from '../../components/Button/Button';
import ActionConfirmation from '../ActionConfirmation/ActionConfirmation';
import validator from '../../helpers/validator';
import {
  NEW,
} from '../../orderStates.js';

const customRowValidation = {
  name: {rule: 'string|required', label: 'Namn'},
  workerTypes: {rule: 'required', label: 'Yrkeskategorier'},
  supplierId: {rule: 'numeric|required', label: 'Leverantör'},
  room: {rule: 'required', label: 'Rum'},
  externalItemNumber: {rule: 'string', label: 'Externt artikelnummer'},
  externalDescription: {rule: 'string', label: 'Beskrivning'},
  internalDescription: {rule: 'string', label: 'Beskrivning'},
  quantity: {rule: 'numeric|min:0', label: 'Antal'},
  price: {rule: 'numeric', label: 'Försäljningspris'},
};

export type Props = {
  clear: Function,
  setDirty: Function,
  reset: Function,
  touch: Function,
  resetForm: Function,
  isDirty: boolean,
  resourcePath: string,
  prevPath: string,
  params: any,
  orderId: number,
  fetchData: Function,
  fetchCustomRow: Function,
  customRow: ?any,
  customRowError: ?any,
  customRowHasLoaded: boolean,
  isFetching: boolean,
  createCustomRow: Function,
  updateCustomRow: Function,
  removeCustomRow: Function,
  customRowFormValues: any,
  customRowFormIsValid: boolean,
  customRowFiles: Object,
  customRowUploads: Object,
  customRowUploadFile: Function,
  customRowAbortFileUpload: Function,
  customRowRemoveFile: Function,
  suppliers: any[],
  productTypes: any[],
  workerTypes: any[],
  roomTypes: any[],
  rooms: any[],
  order: ?any,
};

class CustomRowContainer extends Component<Props> {
  props: Props;

  componentWillMount() {
    const {
      orderId,
      params: {id},
    } = this.props;
    this.props.fetchData(orderId);
    if (id) {
      this.props.fetchCustomRow(id, orderId);
    }
  }

  componentWillUnmount() {
    this.props.clear();
  }

  setDirty() {
    const {
      setDirty,
      isDirty,
    } = this.props;

    if (!isDirty) {
      setDirty(true);
    }
  }

  reset() {
    const {
      setDirty,
      reset,
      resetForm,
    } = this.props;
    reset();
    resetForm('customRow');
    setDirty(false);
  }

  save() {
    const {
      customRowFormValues,
      customRowFormIsValid,
      orderId,
      customRow,
      createCustomRow,
      updateCustomRow,
      customRowFiles,
      touch,
    } = this.props;
    const keys = _.keys(customRowValidation);
    touch('customRow', ...keys);
    if (!customRowFormIsValid) {
      return;
    }
    const data = {
      ...customRowFormValues,
      ...customRowFiles,
    };

    if (!customRow) {
      createCustomRow(orderId, data);
    } else {
      updateCustomRow(customRow.id, data);
    }
  }

  render() {
    const {
      isDirty,
      params: {id},
      prevPath,
      isFetching,
      customRow,
      customRowError,
      customRowHasLoaded,
      customRowFiles,
      customRowUploads,
      customRowUploadFile,
      customRowAbortFileUpload,
      customRowRemoveFile,
      removeCustomRow,
      resourcePath,
      suppliers,
      productTypes,
      workerTypes,
      roomTypes,
      rooms,
      order,
    } = this.props;

    return (
      <div className="relative">
        <div className="row align-justify align-middle content-header">
          <div className="column shrink">
            <div className="row no-row-margins align-middle">
              <Button
                icon="chevron-left" />
              <div className="content-title">
                {(customRow && customRow.name) || 'Ny friprodukt'}
              </div>
            </div>
          </div>
          {customRow && !!order && order.state === NEW &&
            <div className="column shrink horizontal-button-list">
              <ActionConfirmation
                id="removeCustomRow"
                className="danger button"
                message={'Vill du verkligen ta bort den här friprodukten?'}
                buttonText={'Ta bort'}
                action={() => removeCustomRow(customRow.id)} />
            </div>
          }
        </div>
        <LoadingIndicator isLoading={isFetching} />
        <ServerError error={customRowError} />
        <div className="content-section box">
          <CustomRowForm
            showRoomTypes={customRowHasLoaded && customRow && !customRow.room}
            customRowFiles={customRowFiles}
            customRowFileUploads={customRowUploads}
            suppliers={suppliers}
            productTypes={productTypes}
            roomTypes={roomTypes}
            rooms={rooms}
            workerTypes={workerTypes}
            onUploadFile={customRowUploadFile}
            onRemoveFile={customRowRemoveFile}
            onFileUploadAbort={customRowAbortFileUpload}
            initialValues={customRow}
            onUserChange={() => this.setDirty()}
            validate={values => validator(customRowValidation, values)}/>
          {!id && customRow &&
            <Redirect to={`${resourcePath}/${customRow.id}`} />
          }
          {(customRowHasLoaded && !customRow) &&
            <Redirect to={prevPath} />
          }
        </div>
        {isDirty &&
          <SaveMenu
            isLoading={isFetching}
            onDiscard={() => this.reset()}
            onSave={() => this.save()} />
        }
      </div>
    );
  }
}

const getCustomRowUploads = (uploadIds, files) => {
  return _.mapValues(id => files.byId[id], uploadIds) || {};
};

const mapStateToProps = ({
  customRowView: {
    customRow: {
      active: customRow,
      files: customRowFiles,
    },
    uploadIds: customRowUploadIds,
    error: customRowError,
    isFetching: customRowIsFetching,
    dataIsFetching: customRowDataIsFetching,
    isDirty,
    hasLoaded: customRowHasLoaded,
    suppliers: {
      filteredList: suppliers,
    },
    productTypes: {
      filteredList: productTypes,
    },
    workerTypes: {
      filteredList: workerTypes,
    },
    roomTypes: {
      filteredList: roomTypes,
    },
    rooms: {
      filteredList: rooms,
    },
    order,
  },
  form,
  files,
}) => ({
  isDirty,
  customRowFormIsValid: isValid('customRow')({form}),
  customRowFormValues: getFormValues('customRow')({form}),
  customRow,
  customRowFiles,
  customRowUploads: getCustomRowUploads(customRowUploadIds, files),
  customRowError,
  isFetching: customRowIsFetching || customRowDataIsFetching,
  customRowHasLoaded,
  suppliers,
  productTypes,
  workerTypes,
  roomTypes,
  rooms,
  order,
});

const mapDispatchToProps = dispatch => ({
  setDirty: dirty =>
    dispatch(setDirty(dirty, 'customRow')),
  resetForm: form =>
    dispatch(resetForm(form)),
  reset: () =>
    dispatch(reset()),
  touch: (form, ...fields) =>
    dispatch(touch(form, ...fields)),
  clear: () =>
    dispatch(clear()),
  fetchCustomRow: (customRowId, orderId) =>
    dispatch(fetchCustomRow(customRowId, orderId)),
  fetchData: orderId =>
    dispatch(fetchData(orderId)),
  createCustomRow: (orderId, data) =>
    dispatch(createCustomRow(orderId, data)),
  updateCustomRow: (id, data) =>
    dispatch(updateCustomRow(id, data)),
  removeCustomRow: id =>
    dispatch(removeCustomRow(id)),
  customRowRemoveFile: name =>
    dispatch(customRowRemoveFile(name)),
  customRowUploadFile: (name, [file, ...rest]) => {
    if (file) {
      dispatch(customRowUploadFile(name, file));
    }
  },
  customRowAbortFileUpload: tempId =>
    dispatch(customRowAbortFileUpload(tempId)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CustomRowContainer);



// WEBPACK FOOTER //
// ./src/containers/CustomRowContainer/CustomRowContainer.js